import React, { useEffect, useState, Component } from 'react';
import { X, CheckCircle2, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from './ui/Button';
const SUPPORT_EMAIL = 'support@zanoplatform.com';
// Web3Forms delivers submissions straight to your inbox — no email client.
// Get a free access key at https://web3forms.com and set its destination to
// support@zanoplatform.com, then paste the key below.
const WEB3FORMS_ACCESS_KEY = 'daa0e880-90c5-42b7-911c-ac1a7e0e4820';
export const MarketplaceModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reset back to the form whenever the modal is fully closed.
  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setSubmitting(false);
      }, 250);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zano-pink focus:border-zano-pink outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const rainConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#EC4899', '#F9A8D4', '#C4B5FD', '#38BDF8', '#FBCFE8'];
    confetti({
      particleCount: 120,
      spread: 90,
      origin: {
        y: 0.3
      },
      colors
    });
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 70,
        startVelocity: 35,
        origin: {
          x: Math.random(),
          y: -0.1
        },
        gravity: 0.8,
        colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => data.get(k) as string || '—';
    // Send the submission straight to support@zanoplatform.com via Web3Forms.
    const message = ['ABOUT THE USER', `Full name: ${get('fullName')}`, `Work email: ${get('email')}`, `LinkedIn profile URL: ${get('linkedin')}`, `Job title: ${get('jobTitle')}`, `Country: ${get('country')}`, '', 'COMPANY DETAILS', `Company name: ${get('companyName')}`, `Company website: ${get('website')}`, `Business type: ${get('businessType')}`, '', 'CAMPAIGN DETAILS', `Influencer campaigns per month: ${get('campaignsPerMonth')}`, `Estimated monthly budget: ${get('monthlyBudget')}`, `Type of talent looking for: ${get('talentType')}`, '', 'SUBSCRIPTION', `Currently subscribed to the Casting plan: ${get('castingPlan')}`, '', 'OTHER INFO', `How did you hear about Zano: ${get('howDidYouHear')}`, `Referred by a Zano user?: ${get('referredBy')}`, `Anything else?: ${get('notes')}`].join('\n');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New Marketplace Access Request',
      from_name: 'Zano — Request Marketplace Access form',
      replyto: get('email'),
      message
    };
    try {
      setSubmitting(true);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }
      rainConfetti();
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong sending your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-1061" data-mp="2jb407pfpvr7u">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1062" data-mp="2t7zzr5nn1309">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-id="element-1063" data-mp="375ojga5yy43q">
          <X size={20} data-id="element-1064" data-mp="3gw9ifypk1an6" />
        </button>

        {submitted ? <div className="p-8 sm:p-10 text-center" data-id="element-1065" data-mp="3bxy7tlrfqw3w">
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50" data-id="element-1066" data-mp="3bz9j06n7n651">
              <CheckCircle2 size={32} className="text-emerald-500" data-id="element-1067" data-mp="3pvwioh6jsaen" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1068" data-mp="3lukpy2sdn928">
              Request received!
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-1069" data-mp="8dr0n1zyt38q">
              Thank you, we received your request. Our team will get back to you
              within 48 hours. If you are approved, you will receive an
              invitation to create an account as a marketplace user and
              subscribe to a free trial on the marketplace plan. Any questions,
              email us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zano-pink hover:underline" data-id="element-1070" data-mp="3ql6nozvovpm7">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <Button variant="primary" className="w-full sm:w-auto justify-center px-8" onClick={onClose} data-id="element-1071" data-mp="3guvfh14z2u9i">
              Done
            </Button>
          </div> : <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-1072" data-mp="37u5qaga7w24r">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1073" data-mp="1de8gj3y28w39">
              Request Marketplace Access
            </h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed" data-id="element-1074" data-mp="18mpan7h9qzyv">
              Get access to talent agency rosters, represented creator
              profiles, marketplace benchmarks, average pricing insights, and
              the Zano Dashboard. Complete the form below and our team will
              review your request.
            </p>

            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1075" data-mp="1wb06fw83y9ga">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1076" data-mp="1sd2ckg3e6z6k">
                <div data-id="element-1077" data-mp="1nbv8ar0gki4n">
                  <label className={labelClass} data-id="element-1078" data-mp="1nbd1j0g66lih">Full name</label>
                  <input name="fullName" type="text" required className={inputClass} data-id="element-1079" data-mp="1ie3530ud59l5" />
                </div>
                <div data-id="element-1080" data-mp="6xv7ldtsgogc">
                  <label className={labelClass} data-id="element-1081" data-mp="6xmxtusypz9d">Work email</label>
                  <input name="email" type="email" required className={inputClass} data-id="element-1082" data-mp="3t03wxnjsuxwm" />
                </div>
                <div data-id="element-1083" data-mp="6s41tr1yrni8">
                  <label className={labelClass} data-id="element-1084" data-mp="6rm7izyv3jcy">LinkedIn profile URL</label>
                  <input name="linkedin" type="url" required className={inputClass} data-id="element-1085" data-mp="bjvxgfzz8jf8" />
                </div>
                <div data-id="element-1086" data-mp="kj983bzu7e1j">
                  <label className={labelClass} data-id="element-1087" data-mp="kir1blfjthfd">Job title</label>
                  <input name="jobTitle" type="text" required className={inputClass} data-id="element-1088" data-mp="piltrtez2f2j" />
                </div>
                <div data-id="element-1089" data-mp="2rb1pkqe9ipei">
                  <label className={labelClass} data-id="element-1090" data-mp="2rbtiftgh6913">Country</label>
                  <input name="country" type="text" required className={inputClass} data-id="element-1091" data-mp="2w9bg2np1l1ts" />
                </div>
                <div data-id="element-1092" data-mp="x365rj56tfh4">
                  <label className={labelClass} data-id="element-1093" data-mp="x1mrtz707gii">Company name</label>
                  <input name="companyName" type="text" required className={inputClass} data-id="element-1094" data-mp="1braqesnsw12j" />
                </div>
                <div data-id="element-1095" data-mp="j2dagw72agcy">
                  <label className={labelClass} data-id="element-1096" data-mp="j3y6yrcxs9dg">Company website</label>
                  <input name="website" type="url" required className={inputClass} data-id="element-1097" data-mp="e4breb9myh1h" />
                </div>
                <div data-id="element-1098" data-mp="o1gf4fj4qkke">
                  <label className={labelClass} data-id="element-1099" data-mp="o16ocpq9g9dn">
                    What kind of business are you?
                  </label>
                  <select name="businessType" required className={inputClass} data-id="element-1100" data-mp="23gx5l56x6zjp">
                    <option value="" disabled selected data-id="element-1101" data-mp="1zgd9m4y321z5">
                      Select an option
                    </option>
                    <option data-id="element-1102" data-mp="272qrv8v01kl7">Brand</option>
                    <option data-id="element-1103" data-mp="2c0a7od7ci5f9">Marketing Agency</option>
                    <option data-id="element-1104" data-mp="2bxnyk94t3hux">Talent Agency</option>
                  </select>
                </div>
                <div data-id="element-1105" data-mp="1x4b33cjg2nev">
                  <label className={labelClass} data-id="element-1106" data-mp="221kyr9xffvb5">
                    How many influencer campaigns do you run each month?
                  </label>
                  <input name="campaignsPerMonth" type="text" required className={inputClass} data-id="element-1107" data-mp="2psgl3rj45yb0" />
                </div>
                <div data-id="element-1108" data-mp="2g0syc3xi55fv">
                  <label className={labelClass} data-id="element-1109" data-mp="2fzhwi9nma7re">
                    Estimated monthly budget for influencer marketing
                  </label>
                  <input name="monthlyBudget" type="text" required className={inputClass} data-id="element-1110" data-mp="38prh939g8bm8" />
                </div>
                <div className="sm:col-span-2" data-id="element-1111" data-mp="1kvojhr249wl5">
                  <label className={labelClass} data-id="element-1112" data-mp="1ksskdzej91v4">
                    What kind of talent are you looking to work with?
                  </label>
                  <input name="talentType" type="text" placeholder="e.g. creators, celebrities, public figures, athletes, B2B LinkedIn" required className={inputClass} data-id="element-1113" data-mp="1ave9efbdp5a7" />
                </div>
                <div className="sm:col-span-2" data-id="element-1114" data-mp="15y5ymzsk83iz">
                  <label className={labelClass} data-id="element-1115" data-mp="s5y7ponyj6s7">
                    How did you hear about Zano?
                  </label>
                  <select name="howDidYouHear" required className={inputClass} data-id="element-1116" data-mp="x49fltuoz77s">
                    <option value="" disabled selected data-id="element-1117" data-mp="i81xcz2ugfv5">
                      Select an option
                    </option>
                    <option data-id="element-1118" data-mp="n74rvpl773ov">LinkedIn</option>
                    <option data-id="element-1119" data-mp="n8zc11onarrw">YouTube</option>
                    <option data-id="element-1120" data-mp="3kjrhxdjpf75">Google</option>
                    <option data-id="element-1121" data-mp="3lczt2s5kcre">Word of mouth</option>
                    <option data-id="element-1122" data-mp="3vpdsno7q0e9r">WIIM</option>
                    <option data-id="element-1123" data-mp="3vqgkpzgs4mr9">ChatGPT</option>
                    <option data-id="element-1124" data-mp="dizgnut0iee0">Referral from existing user</option>
                    <option data-id="element-1125" data-mp="dkkdjr2su9f1">Influencer Marketing Hub</option>
                    <option data-id="element-1126" data-mp="8jwjsxn6reh4">Cannes Lions 2026</option>
                    <option data-id="element-1127" data-mp="8miu351go831">WebSummit</option>
                    <option data-id="element-1128" data-mp="mj5trfkstccn">Product Hunt</option>
                    <option data-id="element-1129" data-mp="mhuplenfy2l0">Email outreach</option>
                    <option data-id="element-1130" data-mp="ihaqw6n7yqwr">The Creator Network</option>
                    <option data-id="element-1131" data-mp="igj33hzv5xh1">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2" data-id="element-1132" data-mp="wbv2w3svme4t">
                  <label className={labelClass} data-id="element-1133" data-mp="rae36bm4oxpt">
                    Were you referred by a Zano user? If yes, who?
                  </label>
                  <input name="referredBy" type="text" className={inputClass} data-id="element-1134" data-mp="165tyejyg2zns" />
                </div>
                <div className="sm:col-span-2" data-id="element-1135" data-mp="116qtczigstft">
                  <label className={labelClass} data-id="element-1136" data-mp="18ct9pclh1ycm">
                    Anything else you would like us to know?
                  </label>
                  <textarea name="notes" rows={3} className={inputClass} data-id="element-1137" data-mp="1dbeape8jsm24" />
                </div>
              </div>

              <fieldset className="sm:col-span-2" data-id="element-1138" data-mp="1i84qs1kcrhnj">
                <legend className={labelClass} data-id="element-1139" data-mp="1n1zinsa432xp">
                  Are you currently subscribed to the Casting plan?
                </legend>
                <div className="space-y-2" data-id="element-1140" data-mp="1s23peql7wjaq">
                  <div className="flex items-start gap-3" data-id="element-1141" data-mp="1w12zeplt3691">
                    <input type="radio" id="casting-plan-yes" name="castingPlan" value="Yes — subscribed to Casting plan" required className="mt-0.5 shrink-0 border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-1142" data-mp="1w3p5slwynfpo" />
                    <label htmlFor="casting-plan-yes" className="text-sm leading-relaxed text-gray-700" data-id="element-1143" data-mp="bmkcbxw0iypx">
                      Yes — I understand that if approved, my plan will be
                      upgraded to the Marketplace plan ($239/month after the 30
                      day free trial). I can manage or cancel anytime.
                    </label>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1144" data-mp="n1434z6qwvuk">
                    <input type="radio" id="casting-plan-no" name="castingPlan" value="No — not subscribed to Casting plan" className="mt-0.5 shrink-0 border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-1145" data-mp="n0m0nkthple7" />
                    <label htmlFor="casting-plan-no" className="text-sm leading-relaxed text-gray-700" data-id="element-1146" data-mp="2xt5m2iy3x43i">
                      I'm not currently subscribed to the Casting plan
                    </label>
                  </div>
                </div>
              </fieldset>

              <div className="flex items-start gap-3 pt-2" data-id="element-1147" data-mp="q9ud72u16csd">
                <input type="checkbox" id="marketplace-tos" name="tos" required className="mt-1 shrink-0 rounded border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-1148" data-mp="bkpr3ak47oez" />
                <label htmlFor="marketplace-tos" className="text-[11px] text-gray-500 leading-relaxed" data-id="element-1149" data-mp="14a4zdskeb377">
                  By checking this box, I acknowledge and agree to the Terms of
                  Service and Privacy Policy by Zano Platform. I acknowledge
                  that Zano Platform may collect, process, and store my personal
                  data provided in this form for the purpose of evaluating my
                  request and providing access to the platform. I understand
                  that my data will be handled in accordance with the Zano
                  Privacy Policy and GDPR regulations.
                </label>
              </div>

              {error && <p className="text-sm text-red-600" role="alert" data-id="element-1150" data-mp="2b42rjjrd54lo">
                  {error}
                </p>}
              <Button variant="primary" className="w-full justify-center py-3 disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={submitting} data-id="element-1151" data-mp="273ajuuy537ri">
                {submitting ? 'Sending…' : 'Request Marketplace Access'}
              </Button>
            </form>
          </div>}
      </div>
    </div>;
};